/**
 * Data access layer — usecases import from here, not from components or stores directly.
 */
import { apiRepository } from '@/datasource/network/apiRepository';
import { apiSource } from '@/datasource/network/apiSource';
import {
  toAdminSession,
  toAdminUser,
  toAdminGuest,
  toAdminTenantDetail,
  toAdminTenantSummary,
  toConversationDetail,
  toConversationSummary,
  toKnowledgeDetail,
  toKnowledgeDocument,
  toPaginatedFromApiPage,
  toPersona,
} from '@/models/model.map';
import type {
  AdminTenantDetail,
  AdminTenantSummary,
  AuthSession,
  LoginCredentials,
  PaginatedResult,
  User,
  Guest,
} from '@/models/model.type';
import type {
  ConversationDetail,
  ConversationSummary,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  Persona,
} from '@/models/model.type';

export const authRepository = {
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const response = await apiSource.adminLogin(credentials.email.trim(), credentials.password);
    return toAdminSession(response);
  },

  async register(): Promise<AuthSession> {
    throw new Error('Admin registration is not available');
  },

  async logout(): Promise<void> {
    try {
      await apiSource.adminLogout();
    } catch {
      // Stateless logout — clear local session even if BE is unreachable
    }
  },
};

export const usersRepository = {
  async getUsers(
    page: number,
    pageSize: number,
    searchBy?: 'NAME' | 'EMAIL' | 'STATUS',
    query?: string,
  ): Promise<PaginatedResult<User>> {
    const response = await apiSource.getUsers(page, pageSize, searchBy, query);
    return toPaginatedFromApiPage(response, toAdminUser, page, pageSize);
  },

  async patchUser(
    id: string,
    payload: { name?: string; tokenBalance?: number; enabled?: boolean },
  ): Promise<User> {
    const response = await apiSource.patchUser(id, payload);
    return toAdminUser(response);
  },

  async deleteUser(id: string): Promise<void> {
    await apiSource.deleteUser(id);
  },

  async getUserConversations(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<PaginatedResult<ConversationSummary>> {
    const response = await apiSource.getUserConversations(userId, page, pageSize);
    return toPaginatedFromApiPage(response, toConversationSummary, page, pageSize);
  },

  async getConversation(userId: string, conversationId: string): Promise<ConversationDetail> {
    const response = await apiSource.getConversation(userId, conversationId);
    return toConversationDetail(response);
  },

  async deleteConversation(userId: string, conversationId: string): Promise<void> {
    await apiSource.deleteConversation(userId, conversationId);
  },
};

export const guestsRepository = {
  async getGuests(page: number, pageSize: number): Promise<PaginatedResult<Guest>> {
    const response = await apiSource.getGuests(page, pageSize);
    return toPaginatedFromApiPage(response, toAdminGuest, page, pageSize);
  },

  async deleteGuest(id: string): Promise<void> {
    await apiSource.deleteGuest(id);
  },

  async patchGuest(id: string, payload: { tokenBalance?: number }): Promise<Guest> {
    const response = await apiSource.patchGuest(id, payload);
    return toAdminGuest(response);
  },

  async getGuestConversations(
    guestId: string,
    page: number,
    pageSize: number,
  ): Promise<PaginatedResult<ConversationSummary>> {
    const response = await apiSource.getGuestConversations(guestId, page, pageSize);
    return toPaginatedFromApiPage(response, toConversationSummary, page, pageSize);
  },

  async getConversation(guestId: string, conversationId: string): Promise<ConversationDetail> {
    const response = await apiSource.getGuestConversation(guestId, conversationId);
    return toConversationDetail(response);
  },

  async deleteConversation(guestId: string, conversationId: string): Promise<void> {
    await apiSource.deleteGuestConversation(guestId, conversationId);
  },
};

export const tenantsRepository = {
  async list(includeDisabled = true): Promise<AdminTenantSummary[]> {
    const rows = await apiSource.getTenants(includeDisabled);
    return rows.map(toAdminTenantSummary);
  },

  async get(id: string): Promise<AdminTenantDetail> {
    const response = await apiSource.getTenant(id);
    return toAdminTenantDetail(response);
  },

  async create(
    payload: Record<string, unknown>,
  ): Promise<{ tenant: AdminTenantDetail; gatewaySecret: string }> {
    const response = await apiSource.createTenant(payload);
    return {
      tenant: toAdminTenantDetail(response.tenant),
      gatewaySecret: response.gatewaySecret,
    };
  },

  async patch(id: string, payload: Record<string, unknown>): Promise<AdminTenantDetail> {
    const response = await apiSource.patchTenant(id, payload);
    return toAdminTenantDetail(response);
  },
};

export const knowledgeRepository = {
  async list(page: number, pageSize: number): Promise<PaginatedResult<KnowledgeDocument>> {
    const response = await apiSource.getKnowledge(page, pageSize);
    return toPaginatedFromApiPage(response, toKnowledgeDocument, page, pageSize);
  },

  async get(id: string): Promise<KnowledgeDocumentDetail> {
    const response = await apiSource.getKnowledgeDetail(id);
    return toKnowledgeDetail(response);
  },

  async create(payload: {
    title: string;
    content: string;
    sourceUrl?: string | null;
    linkUrl?: string | null;
    aliases?: string | null;
    audience?: 'ALL' | 'AKSARA' | 'SILSILAH';
  }): Promise<KnowledgeDocumentDetail> {
    const response = await apiSource.createKnowledge(payload);
    return toKnowledgeDetail(response);
  },

  async update(
    id: string,
    payload: {
      title: string;
      content: string;
      sourceUrl?: string | null;
      linkUrl?: string | null;
      aliases?: string | null;
      audience?: 'ALL' | 'AKSARA' | 'SILSILAH';
    },
  ): Promise<KnowledgeDocumentDetail> {
    const response = await apiSource.updateKnowledge(id, payload);
    return toKnowledgeDetail(response);
  },

  async delete(id: string): Promise<void> {
    await apiSource.deleteKnowledge(id);
  },

  async getPersona(): Promise<Persona> {
    const response = await apiSource.getPersona();
    return toPersona(response);
  },

  async updatePersona(payload: { name?: string; systemPrompt: string }): Promise<Persona> {
    const response = await apiSource.updatePersona(payload);
    return toPersona(response);
  },
};

export const ordersRepository = {
  getOrders: apiRepository.getOrders.bind(apiRepository),
};

export const dashboardRepository = {
  getDashboard: apiSource.getDashboard.bind(apiSource),
};
